import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Navigation2, Loader2, Gift, Route, Clock3, Wallet, Package, Boxes, Zap, Minus, Plus, Truck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { API } from "../constants/brand";
import { formatEUR } from "../context/CartContext";

const KA = [49.0069, 8.4037];

const BASE_PRICE = 50;
const BASE_KM_INCLUDED = 15;
const RATE_KM = 1.5;          // große Sendungen
const RATE_KM_LONG = 1.2;     // große Sendungen ab 100 km
const LONG_KM_FROM = 100;
const RATE_KM_KLEIN = 0.8;    // Kleinsendungen (Dokumente/Pakete, bis 2 Paletten)
const EXTRA_PRICES = { beladung: 29, entladung: 29, dokumente: 12.9 };
const EXTRA_LABELS = {
  beladung: "Beladung durch Fahrer",
  entladung: "Entladung durch Fahrer",
  dokumente: "Dokumenten-Rücksendung (Papiere)",
};

const FLEET = [
  {
    id: "transit",
    name: "Ford Transit Kasten",
    dims: "L 4,20 m · H 1,70 m",
    note: "Mehrere Fahrzeuge verfügbar",
    image:
      "https://images.unsplash.com/photo-1639416943955-c1281e3c2da3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHZhbiUyMG5pZ2h0JTIwdHJhbnNwb3J0fGVufDB8fHx8MTc5MTQ5NjIxNnww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "movano",
    name: "Opel Movano",
    dims: "L 3,75 m · H 1,70 m",
    note: "Flexibler Allrounder",
    image:
      "https://thumb.wikimedia.org/wikipedia/commons/thumb/4/4e/Opel_Movano_C_1X7A7323.jpg/1280px-Opel_Movano_C_1X7A7323.jpg",
  },
  {
    id: "crafter",
    name: "VW Crafter",
    dims: "L 3,00 m · H 1,70 m",
    note: "Kompakt & schnell",
    image:
      "https://images.unsplash.com/photo-1696592783645-33e966c4c274?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMHZhbiUyMG5pZ2h0JTIwc3RyZWV0JTIwY2FyZ298ZW58MHx8fHwxNzkxODIxNjk1fDA&ixlib=rb-4.1.0&q=85",
  },
];

const CARGO_TYPES = [
  { id: "express", label: "Express-Paket", icon: Zap },
  { id: "pakete", label: "Pakete & Kartons", icon: Package },
  { id: "paletten", label: "Palettenware", icon: Boxes },
];

const dotIcon = (color, label) =>
  L.divIcon({
    className: "",
    html: `<div style="width:30px;height:30px;border-radius:50%;background:${color};border:3px solid #0B0F17;box-shadow:0 0 18px ${color};display:flex;align-items:center;justify-content:center;color:#0B0F17;font-weight:800;font-size:13px;font-family:Outfit,sans-serif;">${label}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });

const haversineKm = (lat1, lon1, lat2, lon2) => {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
};

const shortLabel = (displayName) => displayName.split(",").slice(0, 2).join(",").trim();

async function geocode(q) {
  let res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de&q=${encodeURIComponent(q)}`
  );
  let data = await res.json();
  if (!data.length) {
    res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de&q=${encodeURIComponent(q + ", Deutschland")}`
    );
    data = await res.json();
  }
  if (!data.length) throw new Error(`Adresse nicht gefunden: ${q}`);
  return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon), label: shortLabel(data[0].display_name) };
}

const addressInputCls =
  "w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 transition-colors";

const StructuredAddress = ({ testid, label, icon: Icon, iconClass, onChange, onPick }) => {
  const [plz, setPlz] = useState("");
  const [ort, setOrt] = useState("");
  const [street, setStreet] = useState("");
  const [loadingOrt, setLoadingOrt] = useState(false);

  const emit = (s, p, o) => {
    const combined = [s.trim(), [p.trim(), o.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    onChange(combined);
    onPick(null);
  };

  const fetchCity = async (code) => {
    setLoadingOrt(true);
    try {
      let city = "";
      try {
        const rz = await fetch(`https://api.zippopotam.us/de/${code}`);
        if (rz.ok) {
          const dz = await rz.json();
          city = (dz.places && dz.places[0] && dz.places[0]["place name"]) || "";
        }
      } catch {
        /* fallback mai jos */
      }
      if (!city) {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de&addressdetails=1&postalcode=${code}`
        );
        const d = await r.json();
        const addr = (d[0] && d[0].address) || {};
        city = addr.city || addr.town || addr.village || addr.municipality || "";
      }
      if (city) {
        setOrt(city);
        emit(street, code, city);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingOrt(false);
    }
  };

  const handlePlz = (v) => {
    const clean = v.replace(/\D/g, "").slice(0, 5);
    setPlz(clean);
    emit(street, clean, ort);
    if (clean.length === 5) fetchCity(clean);
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-tech mb-2 flex items-center gap-1.5">
        <Icon size={12} className={iconClass} /> {label}
      </label>
      <div className="grid grid-cols-[100px_1fr] gap-2">
        <input
          data-testid={`${testid}-plz`}
          value={plz}
          onChange={(e) => handlePlz(e.target.value)}
          placeholder="PLZ"
          inputMode="numeric"
          autoComplete="off"
          className={addressInputCls}
        />
        <div className="relative">
          <input
            data-testid={`${testid}-ort`}
            value={ort}
            onChange={(e) => {
              setOrt(e.target.value);
              emit(street, plz, e.target.value);
            }}
            placeholder="Ort — automatisch nach PLZ"
            autoComplete="off"
            className={`${addressInputCls} pr-10`}
          />
          {loadingOrt && (
            <Loader2 size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-cyan-400 animate-spin" />
          )}
        </div>
        <input
          data-testid={`${testid}-strasse`}
          value={street}
          onChange={(e) => {
            setStreet(e.target.value);
            emit(e.target.value, plz, ort);
          }}
          placeholder="Straße & Hausnummer"
          autoComplete="off"
          className={`${addressInputCls} col-span-2`}
        />
      </div>
    </div>
  );
};

const FitRoute = ({ a, b }) => {
  const map = useMap();
  useMemo(() => {
    if (a && b) {
      map.flyToBounds(
        [
          [a.lat, a.lon],
          [b.lat, b.lon],
        ],
        { padding: [60, 60], duration: 1.2 }
      );
    } else if (a) {
      map.flyTo([a.lat, a.lon], 13, { duration: 1 });
    }
  }, [a, b, map]);
  return null;
};

export const RideBooking = () => {
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [pointA, setPointA] = useState(null);
  const [pointB, setPointB] = useState(null);
  const [routeLine, setRouteLine] = useState([]);
  const [km, setKm] = useState(null);
  const [mins, setMins] = useState(null);
  const [vehicle, setVehicle] = useState("transit");
  const [cargoType, setCargoType] = useState("pakete");
  const [pallets, setPallets] = useState(1);
  const [weight, setWeight] = useState("");
  const [pieces, setPieces] = useState("1");
  const [dims, setDims] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("konto_user") || "null");
      if (u) {
        setName((v) => v || u.name || "");
        setEmail((v) => v || u.email || "");
        setPhone((v) => v || u.phone || "");
      }
    } catch {
      /* ignore */
    }
  }, []);
  const [when, setWhen] = useState("");
  const [loyalty, setLoyalty] = useState(null);
  const [booking, setBooking] = useState(false);
  const [extras, setExtras] = useState({ beladung: false, entladung: false, dokumente: false });

  const extrasSum = Object.keys(EXTRA_PRICES).filter((k) => extras[k]).reduce((s, k) => s + EXTRA_PRICES[k], 0);
  const klein = cargoType === "express" || cargoType === "pakete" || (cargoType === "paletten" && pallets <= 2);
  const kmTotal = km != null ? Math.ceil(km) : null;
  const kmMid = kmTotal != null ? Math.max(0, Math.min(kmTotal, LONG_KM_FROM) - BASE_KM_INCLUDED) : 0;
  const kmLong = kmTotal != null ? Math.max(0, kmTotal - LONG_KM_FROM) : 0;
  const kmBillable = kmTotal != null ? Math.max(0, kmTotal - BASE_KM_INCLUDED) : null;
  const distanceCost = klein ? kmBillable * RATE_KM_KLEIN : kmMid * RATE_KM + kmLong * RATE_KM_LONG;
  const price = km != null ? BASE_PRICE + distanceCost + extrasSum : null;
  const mwst = price != null ? Math.round(price * 19) / 100 : null;
  const brutto = price != null ? price + mwst : null;
  const isFreeNext = loyalty?.free_ride_next;

  const calculate = async () => {
    if (!pickup.trim() || !dropoff.trim()) {
      toast.error("Bitte Abholort und Zielort eingeben.");
      return;
    }
    setCalculating(true);
    try {
      const a = pointA && pointA.label === pickup ? pointA : await geocode(pickup);
      const b = pointB && pointB.label === dropoff ? pointB : await geocode(dropoff);
      setPointA(a);
      setPointB(b);
      try {
        const r = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${a.lon},${a.lat};${b.lon},${b.lat}?overview=full&geometries=geojson`
        );
        const j = await r.json();
        const route = j.routes[0];
        setKm(route.distance / 1000);
        setMins(route.duration / 60);
        setRouteLine(route.geometry.coordinates.map(([lo, la]) => [la, lo]));
      } catch {
        const straight = haversineKm(a.lat, a.lon, b.lat, b.lon);
        setKm(straight * 1.35);
        setMins(straight * 1.35 * 2.2 + 4);
        setRouteLine([
          [a.lat, a.lon],
          [b.lat, b.lon],
        ]);
      }
    } catch (e) {
      toast.error(e.message || "Route konnte nicht berechnet werden.");
    } finally {
      setCalculating(false);
    }
  };

  const checkLoyalty = async (value) => {
    setPhone(value);
    if (value.replace(/\D/g, "").length >= 6) {
      try {
        const { data } = await axios.get(`${API}/loyalty`, { params: { phone: value } });
        setLoyalty(data);
      } catch {
        /* ignore */
      }
    }
  };

  const book = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Bitte Name und Telefonnummer angeben.");
      return;
    }
    setBooking(true);
    try {
      const { data } = await axios.post(`${API}/rides`, {
        name,
        phone,
        email: email.trim() || null,
        pickup: pointA?.label || pickup,
        dropoff: pointB?.label || dropoff,
        distance_km: km,
        duration_min: mins ? Math.round(mins) : null,
        vehicle,
        cargo_type: cargoType,
        pallets: cargoType === "paletten" ? pallets : 0,
        pieces: cargoType === "paletten" ? pallets : parseInt(pieces, 10) || null,
        dims: dims.trim() || null,
        weight_kg: weight ? parseInt(weight, 10) : null,
        scheduled_at: when || null,
        extras: Object.keys(EXTRA_PRICES).filter((k) => extras[k]),
      });
      setLoyalty(data.loyalty);
      const mailNote = email.trim() ? " Bestätigung per E-Mail unterwegs." : "";
      if (data.ride.is_free) {
        toast.success(`Transport gebucht — dieser Transport ist KOSTENLOS.${mailNote}`);
      } else {
        toast.success(`Transport gebucht! Preis: ${formatEUR(data.ride.price_eur)}.${mailNote}`);
      }
    } catch {
      toast.error("Buchung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setBooking(false);
    }
  };

  return (
    <section id="fahrt" className="relative py-24 lg:py-32 noise-overlay">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <Eyebrow>Gütertransport · A nach B</Eyebrow>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-50 max-w-2xl">
            Transport buchen — <span className="text-gradient-blue">Preis live berechnet</span>
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl text-base">
            Vom Express-Paket bis zur Palettenware: PLZ eingeben — der Ort wird automatisch ergänzt — Straße dazu,
            Route auf der Karte prüfen, Transporter wählen und direkt buchen. Grundpreis 50 € (inkl. 15 km), danach 0,80–1,50 €/km netto (zzgl. 19 % MwSt.) — deutschlandweit. Dein 11.
            Transport ist kostenlos.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {FLEET.map((v) => (
              <button
                key={v.id}
                data-testid={`fleet-${v.id}`}
                onClick={() => setVehicle(v.id)}
                className={`group relative rounded-2xl overflow-hidden border text-left transition-colors duration-300 ${
                  vehicle === v.id
                    ? "border-cyan-400/60 glow-border"
                    : "border-white/10 hover:border-white/25"
                }`}
              >
                <div className="relative h-28 overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-108"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1320] via-[#0d1320]/30 to-transparent" />
                  {vehicle === v.id && (
                    <span className="absolute top-2.5 right-2.5 rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-2.5 py-0.5 text-[10px] font-bold text-white">
                      Ausgewählt
                    </span>
                  )}
                </div>
                <div className="p-4 bg-[#0d1320]">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-100">
                    <Truck size={15} className="text-cyan-400" /> {v.name}
                  </div>
                  <div className="font-mono-tech text-xs text-cyan-300 mt-1">{v.dims}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{v.note}</div>
                </div>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-6 grid lg:grid-cols-5 gap-6">
          <Reveal className="lg:col-span-3" delay={0.1}>
            <div className="rounded-3xl overflow-hidden border border-white/10 glow-border h-[380px] lg:h-[640px] relative z-0">
              <MapContainer center={KA} zoom={11} scrollWheelZoom className="h-full w-full">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {pointA && <Marker position={[pointA.lat, pointA.lon]} icon={dotIcon("#1E6FD9", "A")} />}
                {pointB && <Marker position={[pointB.lat, pointB.lon]} icon={dotIcon("#29B6F6", "B")} />}
                {routeLine.length > 0 && (
                  <Polyline positions={routeLine} pathOptions={{ color: "#29B6F6", weight: 4, opacity: 0.85 }} />
                )}
                <FitRoute a={pointA} b={pointB} />
              </MapContainer>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-2" delay={0.2}>
            <div className="glass-panel rounded-3xl p-6 lg:p-7 flex flex-col gap-5 h-full">
              <div className="space-y-4">
                <StructuredAddress
                  testid="map-pickup"
                  label="Abholort · Punkt A"
                  icon={MapPin}
                  iconClass="text-[#1E6FD9]"
                  onChange={setPickup}
                  onPick={setPointA}
                />
                <StructuredAddress
                  testid="map-destination"
                  label="Zielort · Punkt B"
                  icon={Navigation2}
                  iconClass="text-cyan-400"
                  onChange={setDropoff}
                  onPick={setPointB}
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono-tech block mb-2">Ladung</label>
                <div className="flex gap-2">
                  {CARGO_TYPES.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`cargo-${c.id}`}
                      onClick={() => setCargoType(c.id)}
                      className={`flex-1 rounded-xl border px-2 py-2.5 text-xs font-semibold transition-colors duration-300 flex items-center justify-center gap-1.5 ${
                        cargoType === c.id
                          ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                          : "border-white/10 text-slate-400 hover:border-white/25"
                      }`}
                    >
                      <c.icon size={13} />
                      {c.label}
                    </button>
                  ))}
                </div>
                {cargoType === "paletten" && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-xl border border-white/10 px-2 py-2">
                      <button
                        data-testid="pallets-minus"
                        onClick={() => setPallets((p) => Math.max(1, p - 1))}
                        className="w-7 h-7 rounded-lg border border-white/10 flex items-center justify-center text-slate-300 hover:border-cyan-400/40"
                      >
                        <Minus size={13} />
                      </button>
                      <span className="font-mono-tech text-sm w-16 text-center text-slate-100" data-testid="pallets-count">
                        {pallets} {pallets === 1 ? "Palette" : "Paletten"}
                      </span>
                      <button
                        data-testid="pallets-plus"
                        onClick={() => setPallets((p) => Math.min(10, p + 1))}
                        className="w-7 h-7 rounded-lg border border-white/10 flex items-center justify-center text-slate-300 hover:border-cyan-400/40"
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                    <input
                      data-testid="cargo-weight-input"
                      type="number"
                      min="0"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Gewicht (kg, optional)"
                      className="flex-1 rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                    />
                    </div>
                    <input
                      data-testid="cargo-dims-input"
                      value={dims}
                      onChange={(e) => setDims(e.target.value)}
                      placeholder="Maße je Palette, z. B. 120 × 80 × 150 cm"
                      className="w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                    />
                  </div>
                )}
                {(cargoType === "express" || cargoType === "pakete") && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <input
                      data-testid="cargo-pieces-input"
                      type="number"
                      min="1"
                      max="50"
                      value={pieces}
                      onChange={(e) => setPieces(e.target.value)}
                      placeholder="Stück"
                      title="Anzahl Pakete"
                      className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                    />
                    <input
                      data-testid="cargo-dims-input"
                      value={dims}
                      onChange={(e) => setDims(e.target.value)}
                      placeholder="Maße L×B×H (cm)"
                      className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                    />
                    <input
                      data-testid="cargo-weight-input"
                      type="number"
                      min="0"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Gewicht (kg)"
                      className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                    />
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-white/10 p-4 space-y-2.5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-tech mb-1">Zusatzoptionen</div>
                {Object.keys(EXTRA_PRICES).map((k) => (
                  <label key={k} data-testid={`extra-${k}`} className="flex items-center justify-between gap-3 cursor-pointer">
                    <span className="flex items-center gap-2.5 text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={extras[k]}
                        onChange={(e) => setExtras({ ...extras, [k]: e.target.checked })}
                        className="w-4 h-4 rounded accent-cyan-400"
                      />
                      {EXTRA_LABELS[k]}
                    </span>
                    <span className="font-mono-tech text-xs text-cyan-300">+{formatEUR(EXTRA_PRICES[k])}</span>
                  </label>
                ))}
                <p className="text-[10px] text-slate-600 leading-snug pt-1">
                  60 Min. Lade- & Entladezeit inklusive — jede weitere angefangene Stunde 40 €.
                </p>
              </div>

              <button
                data-testid="ride-calculate-button"
                onClick={calculate}
                disabled={calculating}
                className="w-full rounded-xl bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] py-3 text-sm font-bold text-white flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
              >
                {calculating ? <Loader2 size={16} className="animate-spin" /> : <Route size={16} />}
                {calculating ? "Route wird berechnet…" : "Route & Preis berechnen"}
              </button>

              {km != null && (
                <div className="grid grid-cols-3 gap-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4">
                  <div>
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500"><Route size={11} /> Distanz</div>
                    <div className="font-mono-tech font-bold text-slate-100 mt-1">{Math.ceil(km)} km</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500"><Clock3 size={11} /> Dauer</div>
                    <div className="font-mono-tech font-bold text-slate-100 mt-1">ca. {Math.round(mins)} Min.</div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500"><Wallet size={11} /> Endpreis</div>
                    <div className="font-mono-tech font-bold text-gradient-blue mt-1">
                      {isFreeNext ? "GRATIS" : formatEUR(brutto)}
                    </div>
                    {!isFreeNext && (
                      <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                        Grundpreis {formatEUR(BASE_PRICE)} (inkl. {BASE_KM_INCLUDED} km)
                        {klein && kmBillable > 0 ? ` + ${kmBillable} km × 0,80 € (Kleinsendung)` : ""}
                        {!klein && kmMid > 0 ? ` + ${kmMid} km × 1,50 €` : ""}
                        {!klein && kmLong > 0 ? ` + ${kmLong} km × 1,20 €` : ""}
                        {extrasSum > 0 ? ` + ${formatEUR(extrasSum)} Zusatzoptionen` : ""}
                        {" "}· zzgl. {formatEUR(mwst)} MwSt. (19 %)
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-white/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-1.5 text-xs text-slate-300"><Gift size={13} className="text-cyan-400" /> Treuebonus</span>
                  <span className="font-mono-tech text-xs text-cyan-300">
                    {loyalty ? `${loyalty.rides_in_cycle}/10 Transporte` : "Telefonnr. eingeben"}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden" data-testid="loyalty-progress-bar">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] transition-[width] duration-700"
                    style={{ width: `${loyalty ? Math.min(loyalty.rides_in_cycle, 10) * 10 : 0}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  {isFreeNext
                    ? "Dein nächster Transport ist KOSTENLOS!"
                    : "Nach 10 Transporten ist der 11. Transport kostenlos."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="ride-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dein Name / Firma"
                  className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                />
                <input
                  data-testid="ride-phone-input"
                  value={phone}
                  onChange={(e) => checkLoyalty(e.target.value)}
                  placeholder="Telefonnummer"
                  className="rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                />
                <input
                  data-testid="ride-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="E-Mail (optional, für Bestätigung)"
                  className="col-span-2 rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50"
                />
                <input
                  data-testid="ride-datetime-input"
                  type="datetime-local"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                  className="col-span-2 rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-300 focus:outline-none focus:border-cyan-400/50 [color-scheme:dark]"
                />
              </div>

              <button
                data-testid="ride-confirm-button"
                onClick={book}
                disabled={km == null || booking}
                className="w-full rounded-xl border border-cyan-400/40 bg-cyan-400/10 py-3 text-sm font-bold text-cyan-300 hover:bg-cyan-400/20 transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {booking && <Loader2 size={16} className="animate-spin" />}
                Transport jetzt buchen
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
